/**
 * WebGL Layer for hardware-accelerated canvas rendering.
 *
 * Renders behind the SVG and HTML layers. Handles:
 * - Infinite grid (dots/lines) with LOD
 * - Textured quad rendering for cached node textures (Phase 0.7)
 *
 * Gracefully falls back if WebGL is unavailable.
 */

import type { Camera, ViewportSize, WorldRect } from './types';
import { TextureCache, type CachedTexture, type TextureCacheStats, type TextureCacheDebugState } from './texture-cache';

/** Grid style options */
export type GridStyle = 'dots' | 'lines' | 'hex' | 'dither' | 'none';

/** WebGL layer options */
export interface WebGLLayerOptions {
	gridStyle?: GridStyle;
	gridSize?: number;
	gridColor?: [number, number, number, number]; // RGBA normalized
	backgroundColor?: [number, number, number, number];
	animateGrid?: boolean;
	maxDPR?: number; // Cap device pixel ratio (default: 2)
	/**
	 * Callback when textures are ready for rendering.
	 * Called at most once per frame, coalesced across all texture completions.
	 * Use this to trigger canvas invalidation/re-render.
	 */
	onTexturesReady?: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<WebGLLayerOptions, 'onTexturesReady'>> & {
	onTexturesReady: (() => void) | undefined;
} = {
	gridStyle: 'hex',
	gridSize: 24,
	gridColor: [0.6, 0.6, 0.6, 0.4], // Subtle mid-gray (works on both light and dark backgrounds)
	backgroundColor: [0.98, 0.98, 0.98, 1.0], // Near white
	animateGrid: true,
	maxDPR: 2,
	onTexturesReady: undefined
};

// Grid vertex shader
const GRID_VERTEX_SHADER = `#version 300 es
precision highp float;

// Quad vertices (fullscreen triangle strip)
in vec2 a_position;

// Output to fragment shader
out vec2 v_worldPos;

// Uniforms
uniform vec2 u_viewport;      // Viewport size in pixels
uniform vec2 u_cameraPos;     // Camera position in world coords
uniform float u_zoom;         // Camera zoom level

void main() {
    // Position is in clip space (-1 to 1)
    gl_Position = vec4(a_position, 0.0, 1.0);

    // Calculate world position for this fragment
    // Convert clip space to normalized [0,1] coordinates
    vec2 normalized = a_position * 0.5 + 0.5;

    // Flip Y: WebGL has Y=0 at bottom, CSS has Y=0 at top
    normalized.y = 1.0 - normalized.y;

    // Screen position in CSS pixels
    vec2 screenPos = normalized * u_viewport;

    // World position: (screenPos - viewport/2) / zoom + cameraPos
    v_worldPos = (screenPos - u_viewport * 0.5) / u_zoom + u_cameraPos;
}
`;

// Grid fragment shader - renders dots
const GRID_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_worldPos;

out vec4 fragColor;

uniform float u_gridSize;     // Base grid size in world units
uniform float u_zoom;         // For LOD calculations
uniform vec4 u_gridColor;     // Grid dot/line color
uniform vec4 u_bgColor;       // Background color
uniform int u_gridStyle;      // 0 = dots, 1 = lines, 2 = none, 3 = dither, 4 = hex
uniform float u_time;         // Time in seconds

// Soft dot rendering with antialiasing
float softDot(vec2 pos, float radius, float softness) {
    float dist = length(pos);
    return 1.0 - smoothstep(radius - softness, radius + softness, dist);
}

// Nearest center for a hex/triangular lattice
vec2 nearestHexCenter(vec2 pos, float size) {
    const float SQRT3 = 1.7320508075688772;

    float m = (2.0 / (size * SQRT3)) * pos.y;
    float n = (pos.x / size) - 0.5 * m;

    vec2 base = floor(vec2(n, m));
    vec2 best = base;
    float bestDist = 1e9;

    for (int i = 0; i < 4; i++) {
        vec2 offset = vec2(float(i & 1), float((i >> 1) & 1));
        vec2 candidate = base + offset;
        vec2 center = vec2(
            size * (candidate.x + 0.5 * candidate.y),
            size * (SQRT3 * 0.5) * candidate.y
        );
        float dist = distance(pos, center);
        if (dist < bestDist) {
            bestDist = dist;
            best = candidate;
        }
    }

    return vec2(
        size * (best.x + 0.5 * best.y),
        size * (SQRT3 * 0.5) * best.y
    );
}

float hexDot(vec2 pos, float level, float radius, float softness) {
    vec2 center = nearestHexCenter(pos, level);
    return softDot(pos - center, radius, softness);
}

float bayer8x8(int x, int y) {
    int idx = y * 8 + x;
    float bayer[64] = float[64](
        0.0 / 64.0, 48.0 / 64.0, 12.0 / 64.0, 60.0 / 64.0, 3.0 / 64.0, 51.0 / 64.0, 15.0 / 64.0, 63.0 / 64.0,
        32.0 / 64.0, 16.0 / 64.0, 44.0 / 64.0, 28.0 / 64.0, 35.0 / 64.0, 19.0 / 64.0, 47.0 / 64.0, 31.0 / 64.0,
        8.0 / 64.0, 56.0 / 64.0, 4.0 / 64.0, 52.0 / 64.0, 11.0 / 64.0, 59.0 / 64.0, 7.0 / 64.0, 55.0 / 64.0,
        40.0 / 64.0, 24.0 / 64.0, 36.0 / 64.0, 20.0 / 64.0, 43.0 / 64.0, 27.0 / 64.0, 39.0 / 64.0, 23.0 / 64.0,
        2.0 / 64.0, 50.0 / 64.0, 14.0 / 64.0, 62.0 / 64.0, 1.0 / 64.0, 49.0 / 64.0, 13.0 / 64.0, 61.0 / 64.0,
        34.0 / 64.0, 18.0 / 64.0, 46.0 / 64.0, 30.0 / 64.0, 33.0 / 64.0, 17.0 / 64.0, 45.0 / 64.0, 29.0 / 64.0,
        10.0 / 64.0, 58.0 / 64.0, 6.0 / 64.0, 54.0 / 64.0, 9.0 / 64.0, 57.0 / 64.0, 5.0 / 64.0, 53.0 / 64.0,
        42.0 / 64.0, 26.0 / 64.0, 38.0 / 64.0, 22.0 / 64.0, 41.0 / 64.0, 25.0 / 64.0, 37.0 / 64.0, 21.0 / 64.0
    );
    return bayer[idx];
}

// Simple smooth noise for subtle animation
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);

    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));

    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Grid LOD: fade between grid levels based on zoom
float getGridAlpha(float gridLevel, float zoom) {
    // Each grid level is visible when screen size of cell is reasonable
    float screenCellSize = gridLevel * zoom;

    // Fade in as screen cell size grows from 4px to 12px
    float fadeIn = smoothstep(4.0, 12.0, screenCellSize);
    // Fade out when cells get very large (1000px to 3000px)
    float fadeOut = 1.0 - smoothstep(1000.0, 3000.0, screenCellSize);

    return fadeIn * fadeOut;
}

void main() {
    // Start transparent — CSS background on .direct-canvas provides the bg color.
    // Grid canvas composites over it via premultiplied alpha.
    fragColor = vec4(0.0);

    if (u_gridStyle == 2) {
        // No grid
        return;
    }

    // Calculate grid levels (powers of gridSize)
    // Single-level grid (consistent spacing)
    float level0 = u_gridSize;

    // Calculate position within the grid level
    vec2 grid0 = mod(v_worldPos, level0);

    // Center the grid cell position (for dot rendering)
    vec2 centered0 = grid0 - level0 * 0.5;

    // Get alpha for zoom-based fade
    float alpha0 = getGridAlpha(level0, u_zoom);

    if (u_gridStyle == 0) {
        // Dots mode — constant screen-size dots
        float radius0 = 1.0 / u_zoom;  // ~1px screen radius regardless of zoom
        float dist = length(centered0);
        float softness = 0.5 / u_zoom;
        float dotAlpha = 1.0 - smoothstep(radius0 - softness, radius0 + softness, dist);

        // Premultiplied alpha output for compositing over CSS background
        float a = dotAlpha * u_gridColor.a * alpha0;
        fragColor = vec4(u_gridColor.rgb * a, a);
    } else if (u_gridStyle == 3) {
        // Ordered dithering (Bayer 8x8)
        vec2 ditherUV = v_worldPos / level0 * 0.7;
        int x = int(mod(floor(ditherUV.x * 8.0), 8.0));
        int y = int(mod(floor(ditherUV.y * 8.0), 8.0));
        float threshold = bayer8x8(x, y);
        float n = noise(ditherUV * 0.08 + vec2(u_time * 0.3, u_time * 0.25)) * 0.4;
        float coverage = n;
        float dotAlpha = step(threshold, coverage);

        // Premultiplied alpha output
        float a = dotAlpha * u_gridColor.a * 0.3 * alpha0;
        fragColor = vec4(u_gridColor.rgb * a, a);
    } else if (u_gridStyle == 4) {
        // Hex dots mode — constant screen-size dots
        float radius0 = 1.0 / u_zoom;  // ~1px screen radius regardless of zoom
        float softness = 0.5 / u_zoom;
        
        float dot0 = hexDot(v_worldPos, level0, radius0, softness) * (alpha0 * 1.1);
        // Premultiplied alpha output
        float a = dot0 * u_gridColor.a;
        fragColor = vec4(u_gridColor.rgb * a, a);
    } else {
        // Lines mode
        float lineWidth0 = 0.5;
        // Distance to nearest grid line
        float dist0 = min(abs(centered0.x), abs(centered0.y));

        // Soft lines
        float lineAlpha = (1.0 - smoothstep(0.0, lineWidth0, dist0));

        // Premultiplied alpha output
        float a = lineAlpha * u_gridColor.a * 0.3 * alpha0;
        fragColor = vec4(u_gridColor.rgb * a, a);
    }
}
`;

// Textured quad vertex shader - for rendering node textures
const QUAD_VERTEX_SHADER = `#version 300 es
precision highp float;

// Per-vertex attributes (unit quad)
in vec2 a_position;  // Unit quad position (0-1)
in vec2 a_texCoord;  // Texture coordinates

// Per-instance attributes
in vec4 a_worldRect;  // x, y, width, height in world coords
in float a_opacity;
in float a_rotation;  // Rotation in radians

// Uniforms
uniform vec2 u_viewport;
uniform vec2 u_cameraPos;
uniform float u_zoom;

// Output to fragment shader
out vec2 v_texCoord;
out float v_opacity;

void main() {
    // worldRect.xy is TOP-LEFT corner, worldRect.zw is width/height
    // Calculate the center of the quad in world coordinates
    vec2 quadCenter = a_worldRect.xy + a_worldRect.zw * 0.5;

    // Local offset from center (a_position is 0-1, so centered is -0.5 to 0.5)
    // Scale by quad size to get world-space offset
    vec2 localOffset = (a_position - vec2(0.5)) * a_worldRect.zw;

    // Apply rotation around the center
    float cos_r = cos(a_rotation);
    float sin_r = sin(a_rotation);
    vec2 rotatedOffset = vec2(
        localOffset.x * cos_r - localOffset.y * sin_r,
        localOffset.x * sin_r + localOffset.y * cos_r
    );

    // Final world position = center + rotated offset
    vec2 worldPos = quadCenter + rotatedOffset;

    // World to screen transform
    vec2 screenPos = (worldPos - u_cameraPos) * u_zoom + u_viewport * 0.5;

    // Screen to clip space
    vec2 clipPos = (screenPos / u_viewport) * 2.0 - 1.0;
    // Flip Y for WebGL
    clipPos.y = -clipPos.y;

    gl_Position = vec4(clipPos, 0.0, 1.0);
    v_texCoord = a_texCoord;
    v_opacity = a_opacity;
}
`;

// Textured quad fragment shader
// Note: Uses premultiplied alpha output for correct compositing with the HTML layer.
// The browser composites WebGL canvases using premultiplied alpha when premultipliedAlpha: true.
const QUAD_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_texCoord;
in float v_opacity;

uniform sampler2D u_texture;

out vec4 fragColor;

void main() {
    vec4 texColor = texture(u_texture, v_texCoord);

    // Apply opacity and premultiply alpha for correct compositing
    float a = texColor.a * v_opacity;
    fragColor = vec4(texColor.rgb * v_opacity, a);
}
`;

/** Quad instance data for batched rendering */
export interface QuadInstance {
	nodeId: string;
	worldRect: WorldRect;
	opacity: number;
	/** Rotation in radians (clockwise from east/right) */
	rotation: number;
}

/**
 * WebGL Layer handles hardware-accelerated rendering.
 *
 * Features:
 * - Shader-based infinite grid with LOD
 * - Textured quad rendering for node textures (Phase 0.7)
 * - Device pixel ratio handling
 * - Graceful fallback detection
 *
 * Architecture:
 * - Grid canvas: positioned behind SVG (for background grid)
 * - Texture canvas: positioned above SVG, below HTML (for node textures)
 * - Both canvases share shaders but have separate GL contexts
 */
export class WebGLLayer {
	private canvas: HTMLCanvasElement; // Grid canvas (behind SVG)
	private textureCanvas: HTMLCanvasElement | null = null; // Texture canvas (above SVG)
	private gl: WebGL2RenderingContext | null = null;
	private textureGl: WebGL2RenderingContext | null = null;
	private options: typeof DEFAULT_OPTIONS;

	// Camera state
	private camera: Camera = { x: 0, y: 0, zoom: 1 };
	private viewport: ViewportSize = { width: 0, height: 0 };
	private dpr: number = 1;

	// Grid shader program
	private gridProgram: WebGLProgram | null = null;
	private gridVAO: WebGLVertexArrayObject | null = null;
	private gridUniforms: {
		viewport: WebGLUniformLocation | null;
		cameraPos: WebGLUniformLocation | null;
		zoom: WebGLUniformLocation | null;
		gridSize: WebGLUniformLocation | null;
		gridColor: WebGLUniformLocation | null;
		bgColor: WebGLUniformLocation | null;
		gridStyle: WebGLUniformLocation | null;
		time: WebGLUniformLocation | null;
	} = {
		viewport: null,
		cameraPos: null,
		zoom: null,
		gridSize: null,
		gridColor: null,
		bgColor: null,
		gridStyle: null,
		time: null
	};

	// Quad shader program (for textured nodes) - uses textureGl context
	private quadProgram: WebGLProgram | null = null;
	private quadVAO: WebGLVertexArrayObject | null = null;
	private quadInstanceBuffer: WebGLBuffer | null = null;
	private quadUniforms: {
		viewport: WebGLUniformLocation | null;
		cameraPos: WebGLUniformLocation | null;
		zoom: WebGLUniformLocation | null;
		texture: WebGLUniformLocation | null;
	} = {
		viewport: null,
		cameraPos: null,
		zoom: null,
		texture: null
	};

	// Texture cache for node textures (uses textureGl context)
	private textureCache: TextureCache | null = null;

	// Reference to container for adding texture canvas later
	private container: HTMLElement;

	// Quad rendering batch
	private quadBatch: QuadInstance[] = [];

	/** Whether WebGL is available and initialized */
	private _available: boolean = false;

	constructor(container: HTMLElement, options?: WebGLLayerOptions) {
		this.options = { ...DEFAULT_OPTIONS, ...options };
		this.container = container;

		// Create grid canvas element (positioned behind SVG)
		this.canvas = document.createElement('canvas');
		this.canvas.classList.add('canvas-webgl-layer', 'canvas-webgl-grid');
		this.canvas.style.position = 'absolute';
		this.canvas.style.top = '0';
		this.canvas.style.left = '0';
		this.canvas.style.width = '100%';
		this.canvas.style.height = '100%';
		this.canvas.style.pointerEvents = 'none';

		container.appendChild(this.canvas);

		// Try to initialize WebGL for grid
		this.initWebGL();
	}

	/** Check if WebGL is available */
	get available(): boolean {
		return this._available;
	}

	private initWebGL(): void {
		// Try WebGL2 first for grid canvas
		this.gl = this.canvas.getContext('webgl2', {
			alpha: true,
			antialias: true,
			premultipliedAlpha: true,
			preserveDrawingBuffer: false
		});

		if (!this.gl) {
			console.warn('WebGL2 not available, falling back to CSS grid');
			this._available = false;
			return;
		}

		this._available = true;

		// Compile grid shader only - texture canvas initialized later via initTextureCanvas()
		this.initGridShader();
	}

	/**
	 * Initialize the texture canvas for rendering node textures.
	 * This canvas should be inserted AFTER the SVG layer so textures appear above edges.
	 *
	 * @param insertAfter - Element after which to insert the texture canvas
	 */
	initTextureCanvas(insertAfter: HTMLElement): void {
		if (this.textureCanvas) return; // Already initialized
		if (!this._available) return; // WebGL not available

		// Create texture canvas (positioned above SVG, below HTML)
		this.textureCanvas = document.createElement('canvas');
		this.textureCanvas.classList.add('canvas-webgl-layer', 'canvas-webgl-textures');
		this.textureCanvas.style.position = 'absolute';
		this.textureCanvas.style.top = '0';
		this.textureCanvas.style.left = '0';
		this.textureCanvas.style.width = '100%';
		this.textureCanvas.style.height = '100%';
		this.textureCanvas.style.pointerEvents = 'none';

		// Insert after the specified element (should be SVG layer)
		insertAfter.after(this.textureCanvas);

		// Initialize WebGL context for texture canvas
		// Note: preserveDrawingBuffer: true is needed for debugging/testing to read pixels
		this.textureGl = this.textureCanvas.getContext('webgl2', {
			alpha: true,
			antialias: true,
			premultipliedAlpha: true,
			preserveDrawingBuffer: true
		});

		if (!this.textureGl) {
			console.warn('WebGL2 not available for texture canvas');
			this.textureCanvas.remove();
			this.textureCanvas = null;
			return;
		}

		// Initialize quad shader and texture cache on texture context
		this.initQuadShader();
		this.textureCache = new TextureCache(this.textureGl, {
			onTexturesReady: this.options.onTexturesReady
		});

		// Apply current viewport size to texture canvas
		this.setViewportSize(this.viewport.width, this.viewport.height);
	}

	/**
	 * Get the texture canvas element for debug overlay.
	 */
	getTextureCanvas(): HTMLCanvasElement | null {
		return this.textureCanvas;
	}

	private initGridShader(): void {
		if (!this.gl) return;

		const gl = this.gl;

		// Compile vertex shader
		const vertShader = gl.createShader(gl.VERTEX_SHADER);
		if (!vertShader) return;
		gl.shaderSource(vertShader, GRID_VERTEX_SHADER);
		gl.compileShader(vertShader);

		if (!gl.getShaderParameter(vertShader, gl.COMPILE_STATUS)) {
			console.error('Grid vertex shader error:', gl.getShaderInfoLog(vertShader));
			this._available = false;
			return;
		}

		// Compile fragment shader
		const fragShader = gl.createShader(gl.FRAGMENT_SHADER);
		if (!fragShader) return;
		gl.shaderSource(fragShader, GRID_FRAGMENT_SHADER);
		gl.compileShader(fragShader);

		if (!gl.getShaderParameter(fragShader, gl.COMPILE_STATUS)) {
			console.error('Grid fragment shader error:', gl.getShaderInfoLog(fragShader));
			this._available = false;
			return;
		}

		// Link program
		this.gridProgram = gl.createProgram();
		if (!this.gridProgram) return;
		gl.attachShader(this.gridProgram, vertShader);
		gl.attachShader(this.gridProgram, fragShader);
		gl.linkProgram(this.gridProgram);

		if (!gl.getProgramParameter(this.gridProgram, gl.LINK_STATUS)) {
			console.error('Grid shader link error:', gl.getProgramInfoLog(this.gridProgram));
			this._available = false;
			return;
		}

		// Clean up individual shaders
		gl.deleteShader(vertShader);
		gl.deleteShader(fragShader);

		// Get uniform locations
		this.gridUniforms = {
			viewport: gl.getUniformLocation(this.gridProgram, 'u_viewport'),
			cameraPos: gl.getUniformLocation(this.gridProgram, 'u_cameraPos'),
			zoom: gl.getUniformLocation(this.gridProgram, 'u_zoom'),
			gridSize: gl.getUniformLocation(this.gridProgram, 'u_gridSize'),
			gridColor: gl.getUniformLocation(this.gridProgram, 'u_gridColor'),
			bgColor: gl.getUniformLocation(this.gridProgram, 'u_bgColor'),
			gridStyle: gl.getUniformLocation(this.gridProgram, 'u_gridStyle'),
			time: gl.getUniformLocation(this.gridProgram, 'u_time')
		};

		// Create fullscreen quad VAO
		this.gridVAO = gl.createVertexArray();
		gl.bindVertexArray(this.gridVAO);

		// Fullscreen triangle strip (2 triangles covering clip space)
		const vertices = new Float32Array([
			-1, -1, 1, -1, -1, 1, 1, 1 // 4 vertices for triangle strip
		]);

		const vbo = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

		const posLoc = gl.getAttribLocation(this.gridProgram, 'a_position');
		gl.enableVertexAttribArray(posLoc);
		gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

		gl.bindVertexArray(null);
	}

	private initQuadShader(): void {
		if (!this.textureGl) return;

		const gl = this.textureGl;

		// Compile vertex shader
		const vertShader = gl.createShader(gl.VERTEX_SHADER);
		if (!vertShader) return;
		gl.shaderSource(vertShader, QUAD_VERTEX_SHADER);
		gl.compileShader(vertShader);

		if (!gl.getShaderParameter(vertShader, gl.COMPILE_STATUS)) {
			console.error('Quad vertex shader error:', gl.getShaderInfoLog(vertShader));
			return;
		}

		// Compile fragment shader
		const fragShader = gl.createShader(gl.FRAGMENT_SHADER);
		if (!fragShader) return;
		gl.shaderSource(fragShader, QUAD_FRAGMENT_SHADER);
		gl.compileShader(fragShader);

		if (!gl.getShaderParameter(fragShader, gl.COMPILE_STATUS)) {
			console.error('Quad fragment shader error:', gl.getShaderInfoLog(fragShader));
			return;
		}

		// Link program
		this.quadProgram = gl.createProgram();
		if (!this.quadProgram) return;
		gl.attachShader(this.quadProgram, vertShader);
		gl.attachShader(this.quadProgram, fragShader);
		gl.linkProgram(this.quadProgram);

		if (!gl.getProgramParameter(this.quadProgram, gl.LINK_STATUS)) {
			console.error('Quad shader link error:', gl.getProgramInfoLog(this.quadProgram));
			return;
		}

		// Clean up individual shaders
		gl.deleteShader(vertShader);
		gl.deleteShader(fragShader);

		// Get uniform locations
		this.quadUniforms = {
			viewport: gl.getUniformLocation(this.quadProgram, 'u_viewport'),
			cameraPos: gl.getUniformLocation(this.quadProgram, 'u_cameraPos'),
			zoom: gl.getUniformLocation(this.quadProgram, 'u_zoom'),
			texture: gl.getUniformLocation(this.quadProgram, 'u_texture')
		};

		// Create VAO for instanced quad rendering
		this.quadVAO = gl.createVertexArray();
		gl.bindVertexArray(this.quadVAO);

		// Unit quad vertices (position and texCoord interleaved)
		// Position (0-1), TexCoord (0-1)
		//
		// Coordinate system notes:
		// - World coords: Y-down (CSS convention), so node.position.y is at TOP of node
		// - Vertex shader flips Y for WebGL clip space
		// - Texture was captured from HTML canvas (Y=0 at top)
		// - WebGL textures: V=0 at bottom by default
		//
		// The quad position (0,0) maps to worldRect.xy which is the TOP-LEFT of the node.
		// After the Y flip, this becomes the TOP of screen. We want to sample the TOP
		// of the original HTML content there, which is at V=0 in the flipped texture.
		const quadVertices = new Float32Array([
			// Triangle 1
			0, 0, 0, 0, // position bottom-left → maps to node top-left → texCoord top-left
			1, 0, 1, 0, // position bottom-right → maps to node top-right → texCoord top-right
			0, 1, 0, 1, // position top-left → maps to node bottom-left → texCoord bottom-left
			// Triangle 2
			1, 0, 1, 0, // position bottom-right → maps to node top-right → texCoord top-right
			1, 1, 1, 1, // position top-right → maps to node bottom-right → texCoord bottom-right
			0, 1, 0, 1 // position top-left → maps to node bottom-left → texCoord bottom-left
		]);

		const quadVBO = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, quadVBO);
		gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);

		// Position attribute
		const posLoc = gl.getAttribLocation(this.quadProgram, 'a_position');
		gl.enableVertexAttribArray(posLoc);
		gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0);

		// TexCoord attribute
		const texLoc = gl.getAttribLocation(this.quadProgram, 'a_texCoord');
		gl.enableVertexAttribArray(texLoc);
		gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 16, 8);

		// Instance buffer for world rects, opacity, and rotation
		this.quadInstanceBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadInstanceBuffer);

		// worldRect (vec4) + opacity (float) + rotation (float) = 24 bytes per instance
		const stride = 24;

		const rectLoc = gl.getAttribLocation(this.quadProgram, 'a_worldRect');
		gl.enableVertexAttribArray(rectLoc);
		gl.vertexAttribPointer(rectLoc, 4, gl.FLOAT, false, stride, 0);
		gl.vertexAttribDivisor(rectLoc, 1);

		const opacityLoc = gl.getAttribLocation(this.quadProgram, 'a_opacity');
		gl.enableVertexAttribArray(opacityLoc);
		gl.vertexAttribPointer(opacityLoc, 1, gl.FLOAT, false, stride, 16);
		gl.vertexAttribDivisor(opacityLoc, 1);

		const rotationLoc = gl.getAttribLocation(this.quadProgram, 'a_rotation');
		gl.enableVertexAttribArray(rotationLoc);
		gl.vertexAttribPointer(rotationLoc, 1, gl.FLOAT, false, stride, 20);
		gl.vertexAttribDivisor(rotationLoc, 1);

		gl.bindVertexArray(null);
	}

	/**
	 * Set the camera for rendering.
	 */
	setCamera(camera: Camera): void {
		this.camera = camera;
	}

	/**
	 * Set viewport size and handle DPR.
	 */
	setViewportSize(width: number, height: number): void {
		this.viewport = { width, height };

		// Handle device pixel ratio (capped)
		this.dpr = Math.min(window.devicePixelRatio || 1, this.options.maxDPR);

		// Set grid canvas size (CSS size and buffer size)
		this.canvas.style.width = `${width}px`;
		this.canvas.style.height = `${height}px`;
		this.canvas.width = Math.floor(width * this.dpr);
		this.canvas.height = Math.floor(height * this.dpr);

		// Update WebGL viewport for grid
		if (this.gl) {
			this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
		}

		// Set texture canvas size if it exists
		if (this.textureCanvas) {
			this.textureCanvas.style.width = `${width}px`;
			this.textureCanvas.style.height = `${height}px`;
			this.textureCanvas.width = Math.floor(width * this.dpr);
			this.textureCanvas.height = Math.floor(height * this.dpr);

			if (this.textureGl) {
				this.textureGl.viewport(0, 0, this.textureCanvas.width, this.textureCanvas.height);
			}
		}
	}

	/**
	 * Set grid options.
	 */
	setGridOptions(options: Partial<WebGLLayerOptions>): void {
		this.options = { ...this.options, ...options };
	}

	isAnimatedGrid(): boolean {
		return this.options.animateGrid === true && this.options.gridStyle === 'dither';
	}

	/**
	 * Render the grid.
	 */
	renderGrid(): void {
		if (!this.gl || !this.gridProgram || !this.gridVAO) return;

		const gl = this.gl;

		// Clear to transparent — CSS background on .direct-canvas provides the bg
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);

		// Use grid shader
		gl.useProgram(this.gridProgram);
		gl.bindVertexArray(this.gridVAO);

		// Set uniforms
		// Note: viewport in shader is in CSS pixels (not device pixels) for world coord calculations
		gl.uniform2f(this.gridUniforms.viewport, this.viewport.width, this.viewport.height);
		gl.uniform2f(this.gridUniforms.cameraPos, this.camera.x, this.camera.y);
		gl.uniform1f(this.gridUniforms.zoom, this.camera.zoom);
		gl.uniform1f(this.gridUniforms.gridSize, this.options.gridSize);
		gl.uniform4fv(this.gridUniforms.gridColor, this.options.gridColor);
		gl.uniform4fv(this.gridUniforms.bgColor, this.options.backgroundColor);
		gl.uniform1f(this.gridUniforms.time, performance.now() * 0.001);

		// Grid style: 0 = dots, 1 = lines, 2 = none, 3 = dither, 4 = hex
		const styleInt =
			this.options.gridStyle === 'dots'
				? 0
				: this.options.gridStyle === 'lines'
					? 1
					: this.options.gridStyle === 'dither'
						? 3
						: this.options.gridStyle === 'hex'
							? 4
							: 2;
		gl.uniform1i(this.gridUniforms.gridStyle, styleInt);

		// Draw fullscreen quad
		gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

		gl.bindVertexArray(null);
	}

	/**
	 * Get the WebGL context for advanced usage.
	 */
	getContext(): WebGL2RenderingContext | null {
		return this.gl;
	}

	/**
	 * Get the canvas element.
	 */
	getCanvas(): HTMLCanvasElement {
		return this.canvas;
	}

	// ===========================================================================
	// Texture Cache API (Phase 0.7)
	// ===========================================================================

	/**
	 * Check if a texture exists for a node.
	 */
	hasTexture(nodeId: string): boolean {
		return this.textureCache?.hasTexture(nodeId) ?? false;
	}

	/**
	 * Check if a texture exists and is valid (matches content hash).
	 */
	hasValidTexture(nodeId: string, contentHash: string): boolean {
		return this.textureCache?.hasValidTexture(nodeId, contentHash) ?? false;
	}

	/**
	 * Request texture generation for a node.
	 *
	 * @param nodeId - The node ID (compound: "actualNodeId:lodLevel")
	 * @param getElement - Callback to get the HTML element to capture
	 * @param contentHash - Hash of content for cache invalidation
	 * @param priority - Higher priority = generated sooner (default: 0)
	 * @param expectedLOD - Expected LOD level at capture time (for race condition validation)
	 * @param targetSize - Target max dimension in pixels (default: textureThreshold)
	 */
	requestTexture(
		nodeId: string,
		getElement: () => HTMLElement | null,
		contentHash: string,
		priority: number = 0,
		expectedLOD?: string,
		targetSize?: number
	): void {
		this.textureCache?.requestTexture({
			nodeId,
			getElement,
			contentHash,
			priority,
			expectedLOD,
			targetSize
		});
	}

	/**
	 * Invalidate a cached texture (e.g., when content changes).
	 */
	invalidateTexture(nodeId: string): void {
		this.textureCache?.invalidate(nodeId);
	}

	/**
	 * Get texture cache statistics.
	 */
	getTextureCacheStats(): TextureCacheStats | null {
		return this.textureCache?.getStats() ?? null;
	}

	/**
	 * Get texture cache debug state for visualization.
	 */
	getTextureDebugState(): TextureCacheDebugState | null {
		return this.textureCache?.getDebugState() ?? null;
	}

	/**
	 * Get texture resolution multiplier.
	 */
	getTextureResolutionMultiplier(): number {
		return this.textureCache?.getTextureResolutionMultiplier() ?? 1.0;
	}

	/**
	 * Set texture resolution multiplier.
	 * @param multiplier - Multiplier for texture target size (0.1 to 4.0)
	 * @param invalidateExisting - If true, invalidate all existing textures
	 */
	setTextureResolutionMultiplier(multiplier: number, invalidateExisting: boolean = false): void {
		this.textureCache?.setTextureResolutionMultiplier(multiplier);
		if (invalidateExisting) {
			this.textureCache?.invalidateAll();
		}
	}

	/**
	 * Get default target size for textures.
	 */
	getDefaultTargetSize(): number {
		return this.textureCache?.getDefaultTargetSize() ?? 150;
	}

	/**
	 * Set default target size for textures.
	 * @param size - Target size in pixels (32 to 1024)
	 * @param invalidateExisting - If true, invalidate all existing textures
	 */
	setDefaultTargetSize(size: number, invalidateExisting: boolean = false): void {
		this.textureCache?.setDefaultTargetSize(size);
		if (invalidateExisting) {
			this.textureCache?.invalidateAll();
		}
	}

	/**
	 * Check if a node has a pending texture request.
	 */
	isTexturePending(nodeId: string): boolean {
		return this.textureCache?.isPending(nodeId) ?? false;
	}

	/**
	 * Check if a node was recently skipped during texture generation.
	 */
	wasTextureSkipped(nodeId: string): boolean {
		return this.textureCache?.wasRecentlySkipped(nodeId) ?? false;
	}

	/**
	 * Get texture preview thumbnails for debug inspection.
	 */
	getTexturePreviews(
		maxCount?: number,
		thumbnailSize?: number
	): Array<{
		nodeId: string;
		width: number;
		height: number;
		memoryBytes: number;
		dataUrl: string;
	}> {
		return this.textureCache?.getTexturePreviews(maxCount, thumbnailSize) ?? [];
	}

	/**
	 * Draw a cached texture for a node.
	 * Returns true if the texture was drawn, false if not available.
	 */
	drawCachedTexture(nodeId: string, worldRect: WorldRect, opacity: number = 1, rotation: number = 0): boolean {
		// Check textureGl (not gl) since that's where quad program and textures live
		if (!this.textureCache || !this.textureGl || !this.quadProgram || !this.quadVAO) {
			return false;
		}

		const cached = this.textureCache.getTexture(nodeId);
		if (!cached) {
			return false;
		}

		// Add to batch for rendering
		this.quadBatch.push({ nodeId, worldRect, opacity, rotation });
		return true;
	}

	/**
	 * Render all batched textured quads.
	 * Call this after all drawCachedTexture calls for the frame.
	 */
	renderTexturedQuads(): void {
		if (!this.textureGl) {
			return;
		}

		const gl = this.textureGl;

		// ALWAYS clear the texture canvas (even if no textures to draw)
		// This ensures old textures don't persist when transitioning to HTML rendering
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);

		// Early return if nothing to draw (but canvas is now cleared)
		if (!this.quadProgram || !this.quadVAO || this.quadBatch.length === 0) {
			return;
		}

		// Enable blending for transparency
		// Use premultiplied alpha blending - the fragment shader outputs premultiplied colors
		// and the canvas context has premultipliedAlpha: true
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

		// Use quad shader
		gl.useProgram(this.quadProgram);
		gl.bindVertexArray(this.quadVAO);

		// Set common uniforms
		gl.uniform2f(this.quadUniforms.viewport, this.viewport.width, this.viewport.height);
		gl.uniform2f(this.quadUniforms.cameraPos, this.camera.x, this.camera.y);
		gl.uniform1f(this.quadUniforms.zoom, this.camera.zoom);
		gl.uniform1i(this.quadUniforms.texture, 0);

		// Render each quad individually (could be optimized to batch by texture later)
		for (const quad of this.quadBatch) {
			const cached = this.textureCache!.getTexture(quad.nodeId);
			if (!cached) continue;

			// Bind texture
			gl.activeTexture(gl.TEXTURE0);
			gl.bindTexture(gl.TEXTURE_2D, cached.texture);

			// Update instance buffer with single instance
			// worldRect (x, y, width, height) + opacity + rotation = 6 floats
			const instanceData = new Float32Array([
				quad.worldRect.x,
				quad.worldRect.y,
				quad.worldRect.width,
				quad.worldRect.height,
				quad.opacity,
				quad.rotation
			]);

			gl.bindBuffer(gl.ARRAY_BUFFER, this.quadInstanceBuffer);
			gl.bufferData(gl.ARRAY_BUFFER, instanceData, gl.DYNAMIC_DRAW);

			// Draw single instanced quad
			gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, 1);
		}

		gl.bindVertexArray(null);
		gl.disable(gl.BLEND);

		// Clear batch for next frame
		this.quadBatch = [];
	}

	/**
	 * Clean up resources.
	 */
	destroy(): void {
		// Clean up grid GL context
		if (this.gl) {
			if (this.gridProgram) {
				this.gl.deleteProgram(this.gridProgram);
			}
			if (this.gridVAO) {
				this.gl.deleteVertexArray(this.gridVAO);
			}
		}

		// Clean up texture GL context
		if (this.textureGl) {
			if (this.quadProgram) {
				this.textureGl.deleteProgram(this.quadProgram);
			}
			if (this.quadVAO) {
				this.textureGl.deleteVertexArray(this.quadVAO);
			}
			if (this.quadInstanceBuffer) {
				this.textureGl.deleteBuffer(this.quadInstanceBuffer);
			}
		}

		// Clean up texture cache
		this.textureCache?.destroy();
		this.textureCache = null;

		// Remove canvases
		this.canvas.remove();
		this.textureCanvas?.remove();

		this.gl = null;
		this.textureGl = null;
		this.textureCanvas = null;
		this._available = false;
	}
}

/**
 * Check if WebGL2 is available in this browser.
 */
export function isWebGL2Available(): boolean {
	try {
		const canvas = document.createElement('canvas');
		const gl = canvas.getContext('webgl2');
		return gl !== null;
	} catch {
		return false;
	}
}
